import { useEffect, useMemo, useState } from "react";
import { fetchPostMultipart } from "../fetchers";

// Bisnis Unit & Bagian user yang sedang login.
//
// localStorage "user" hanyalah SNAPSHOT saat login dan tidak pernah disegarkan:
// sesi lama bisa tidak punya "BisnisUnitID" sama sekali, dan — kalau browser
// masih memegang sesi akun lain — "BagianID"-nya bisa MILIK ORANG LAIN. Karena
// itu server selalu ditanya ulang lewat POST getDetailKaryawan (FormData Nik);
// nilai cache hanya dipakai sebagai tebakan awal supaya UI tak berkedip.
// CheckAuth backend no-op, jadi endpoint ini bisa dipanggil semua user.
//
// Mengembalikan { bisnisUnitID, bagianID, resolved }:
//   resolved=false selama BU belum diketahui sama sekali (tak ada cache dan
//   fetch masih jalan) — pemanggil yang memakai BU untuk membatasi akses
//   sebaiknya menunggu resolved sebelum melonggarkan pembatasan. Kalau cache
//   ada, resolved langsung true dan nilainya dikoreksi begitu server menjawab.
export const useUserBisnisUnit = () => {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null") || {};
    } catch {
      return {};
    }
  }, []);

  const [bisnisUnitID, setBisnisUnitID] = useState(
    currentUser?.BisnisUnitID || "",
  );
  const [bagianID, setBagianID] = useState(currentUser?.BagianID || "");
  const [resolved, setResolved] = useState(!!currentUser?.BisnisUnitID);

  useEffect(() => {
    if (!currentUser?.Nik) {
      setResolved(true);
      return;
    }
    let alive = true;
    const fd = new FormData();
    fd.append("Nik", currentUser.Nik);
    fetchPostMultipart("getDetailKaryawan", fd)
      .then((res) => {
        if (!alive) return;
        const row = res?.data?.[0];
        if (!row) return;
        if (row.BisnisUnitID) setBisnisUnitID(row.BisnisUnitID);
        if (row.BagianID) setBagianID(row.BagianID);
        syncStoredUser(currentUser.Nik, row);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setResolved(true);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { bisnisUnitID, bagianID, resolved };
};

// Tulis balik BU/Bagian hasil server ke localStorage supaya halaman lain yang
// masih membaca snapshot login langsung ikut sembuh (tanpa harus login ulang).
// Nik dicocokkan dulu: kalau sesi sudah berganti di tab lain, jangan timpa.
const syncStoredUser = (nik, row) => {
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    if (!stored || stored.Nik !== nik) return;
    if (
      stored.BisnisUnitID === row.BisnisUnitID &&
      stored.BagianID === row.BagianID
    ) {
      return;
    }
    localStorage.setItem(
      "user",
      JSON.stringify({
        ...stored,
        BisnisUnitID: row.BisnisUnitID || stored.BisnisUnitID,
        BagianID: row.BagianID || stored.BagianID,
      }),
    );
  } catch {
    /* localStorage tak bisa ditulis — biarkan, state hook tetap benar */
  }
};

export default useUserBisnisUnit;
